<template>
  <div>
    <div>
      <!-- Mobile menu overlay -->
      <div
        v-show="sidebarOpen"
        class="relative z-50"
        role="dialog"
        aria-modal="true"
      >
        <!-- Backdrop -->
        <div
          class="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
        ></div>

        <div class="fixed inset-0 z-40 flex">
          <!-- Mobile sidebar -->
          <div
            class="relative flex w-full max-w-xs flex-1 flex-col bg-gray-700 pt-5 pb-4 transition-transform"
          >
            <!-- Close button for mobile -->
            <div class="absolute top-0 right-0 -mr-12 pt-2">
              <button
                @click="sidebarOpen = false"
                type="button"
                class="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                <span class="sr-only">Close sidebar</span>
                <svg
                  class="h-6 w-6 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <!-- Logo -->
            <NuxtLink class="flex flex-shrink-0 items-center px-4" to="/">
              <img src="/logo.svg" alt="Logo" class="h-18 mx-auto" />
            </NuxtLink>

            <!-- Mobile navigation menu -->
            <div class="mt-5 h-0 flex-1 overflow-y-auto">
              <nav class="space-y-2 px-2">
                <button
                  @click="fetch"
                  class="paginationButtonIcon w-full text-white hover:bg-primary-600 flex items-center px-2 py-2 text-base font-medium rounded-md"
                >
                  <svg
                    class="mr-2 h-7 w-7 flex-shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                    />
                  </svg>
                  Refresh Menu
                </button>

                <!-- Sign in link when not logged in -->
                <ClientOnly>
                  <nuxt-link
                    v-if="!isLoggedIn"
                    exact-active-class="bg-primary-800"
                    to="/signin"
                    class="text-white hover:bg-primary-600 group flex items-center px-2 py-2 text-base font-medium rounded-md"
                  >
                    Sign In
                  </nuxt-link>

                  <!-- Menu items when logged in -->
                  <div v-if="isLoggedIn">
                    <nuxt-link
                      v-for="m in menuItems.filter((m) => m.MenuGroup == null)"
                      :key="`mobile-${m.Id}`"
                      :to="m.Link"
                      exact-active-class="bg-primary-800"
                      class="text-white hover:bg-primary-600 group flex items-center px-2 py-2 text-base font-medium rounded-md mb-1"
                    >
                      <div class="mr-2 w-7 h-7" v-html="m.Icon"></div>
                      <p class="text-lg">{{ m.Name }}</p>
                    </nuxt-link>
                  </div>
                </ClientOnly>
              </nav>
            </div>
          </div>

          <div class="w-14 flex-shrink-0"></div>
        </div>
      </div>

      <!-- Desktop sidebar -->
      <div
        class="hidden md:fixed md:inset-y-0 md:flex md:flex-col z-10"
        :class="{ 'md:w-64': !iconOnly, 'md:w-20': iconOnly }"
      >
        <!-- Sidebar component -->
        <div class="flex min-h-0 flex-1 flex-col bg-gray-700">
          <!-- Logo and toggle button -->
          <div class="flex items-center h-16 flex-shrink-0 px-4 bg-gray-700">
            <NuxtLink
              v-if="!iconOnly"
              class="text-white text-2xl font-semibold"
              to="/"
            >
              <img src="/logo.svg" alt="Logo" class="h-32 mx-auto" />
            </NuxtLink>
            <button
              @click="toggleIcons"
              class="text-white ml-auto p-1"
              :title="iconOnly ? 'Expand sidebar' : 'Collapse sidebar'"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6"
                v-if="!iconOnly"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5"
                />
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6"
                v-else
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M11.25 4.5l7.5 7.5-7.5 7.5m-6-15l7.5 7.5-7.5 7.5"
                />
              </svg>
            </button>
          </div>

          <hr class="border-white/20" />

          <!-- Desktop navigation menu -->
          <div class="flex flex-1 flex-col overflow-y-auto">
            <nav class="flex-1 space-y-1 px-2 py-4">
              <!-- Refresh menu button -->
              <button
                @click="fetch"
                class="w-full py-2 border-t border-b border-ppicon text-ppicon bg-white group flex items-center px-2 text-sm font-medium cursor-pointer"
                :class="iconOnly ? 'justify-center' : ''"
              >
                <svg
                  class="h-6 w-6 flex-shrink-0"
                  :class="iconOnly ? '' : 'mr-3'"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                  />
                </svg>
                <span v-if="!iconOnly">Refresh Menu</span>
              </button>

              <!-- Menu items when logged in -->
              <ClientOnly>
                <div v-if="isLoggedIn">
                  <div class="flex items-center px-2 py-2">
                    <UiClickToSearch v-model:search="searchTerm" />
                  </div>
                  <nuxt-link
                    v-for="m in filteredMenuItems"
                    :key="`main-${m.Id}`"
                    :to="m.Link"
                    :class="{
                      'hover:bg-transparent hover:border-b-4 hover:border-ppeventable group flex items-center px-2 py-2 text-sm font-medium border-b-2 border-transparent c-tooltip relative':
                        iconStyle,
                      'text-white hover:bg-white hover:text-ppicon group flex items-center px-2 py-2 text-sm font-medium rounded-md':
                        !iconStyle,
                    }"
                    exact-active-class="bg-ppicon"
                    :aria-label="m.Name"
                  >
                    <div class="group flex relative">
                      <div
                        :class="{ 'mx-auto': iconOnly }"
                        class="w-7 h-7"
                        v-html="m.Icon"
                        v-if="m.Icon"
                      ></div>
                      <div
                        class="w-7 h-7 flex justify-center items-center"
                        v-else
                      >
                        <i class="pi pi-asterisk text-slate-400 text-xl"></i>
                      </div>
                    </div>
                    <span v-if="!iconOnly" class="text-sm ml-3">{{
                      m.Name
                    }}</span>
                  </nuxt-link>
                </div>
              </ClientOnly>
            </nav>
          </div>
        </div>
      </div>
      <!-- Main content area -->
      <div class="flex flex-1 flex-col" :class="responsiveClasses">
        <!-- Top navigation bar -->
        <div class="sticky top-0 z-40 flex h-16 flex-shrink-0 bg-white shadow">
          <!-- Mobile menu button -->
          <button
            @click="sidebarOpen = !sidebarOpen"
            type="button"
            class="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
          >
            <span class="sr-only">Open sidebar</span>
            <svg
              class="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12"
              />
            </svg>
          </button>
          <!-- Header content -->
          <div class="flex flex-1 justify-between px-4" id="navigation-header">
            <div class="flex flex-1 flex-row gap-2 items-center">
              <template v-if="menuGroup">
                <NuxtLink
                  class="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
                  :to="{
                    path: `/internal/${menuGroup}`,
                  }"
                >
                  <i class="pi pi-arrow-left text-base" />
                </NuxtLink>
              </template>
              <template v-if="pageTitle">
                <i :class="pageIcon" style="font-size: 1.5rem" />
                <h1 class="text-lg font-semibold text-gray-900">
                  {{ pageTitle }}
                </h1>
              </template>
            </div>
            <div class="ml-4 flex items-center md:ml-6">
              <ClientOnly>
                <!-- Sign In button for non-logged in users -->
                <nuxt-link
                  v-if="!isLoggedIn"
                  to="/signin"
                  class="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  Sign In
                </nuxt-link>
              </ClientOnly>

              <!-- Notifications button -->
              <!-- <button
                type="button"
                class="rounded-full bg-white p-1 text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <span class="sr-only">View notifications</span>
                <svg
                  class="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                  />
                </svg>
              </button> -->

              <!-- Profile dropdown -->
              <ClientOnly>
              <div class="relative ml-3 z-50" v-if="isLoggedIn">
                <div>
                  <button
                    @click="showDropdownMenu = !showDropdownMenu"
                    type="button"
                    class="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    id="user-menu-button"
                  >
                    <span class="sr-only">Open user menu</span>
                    <img
                      class="h-8 w-8 rounded-full"
                      :src="user?.ProfileUrl"
                      alt="Profile"
                    />
                  </button>
                </div>

                <!-- Dropdown menu -->
                <div
                  v-show="showDropdownMenu"
                  class="absolute right-0 z-50 mt-2 w-max min-w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu-button"
                >
                  <div class="flex flex-col px-4 py-2">
                    <div class="text-base font-semibold">
                      {{ user?.DisplayName }}
                    </div>
                    <div class="text-sm text-gray-400">
                      {{ user?.UserName }}
                    </div>
                  </div>
                  <nuxt-link
                    @click.native="closeDropdown"
                    to="/signout"
                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                    v-if="isLoggedIn"
                  >
                    Sign out
                  </nuxt-link>
                  <nuxt-link
                    @click.native="closeDropdown"
                    to="/signin"
                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                    v-else
                  >
                    Sign in
                  </nuxt-link>
                </div>
              </div>
              </ClientOnly>
            </div>
          </div>
        </div>

        <!-- Main content -->
        <main class="flex-1">
          <!-- <error-message></error-message> -->
          <slot></slot>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Define menu item interface
interface MenuItem {
  Id: string | number;
  Name: string;
  Link: string;
  Icon: string;
  MenuGroup: string | null;
}

// State
const sidebarOpen = ref(false);
const showDropdownMenu = ref(false);
const menuItems = ref<MenuItem[]>([]);
const iconOnly = ref(false);
const iconStyle = ref(false);
const isDesktop = ref(false); // Start as false to match mobile-first approach

// Auth store
const { loggedIn: isLoggedIn, user } = useAuthStore();

// Safe computed for responsive classes to prevent hydration mismatch
const responsiveClasses = computed(() => {
  // Always return consistent classes for SSR
  if (import.meta.server) {
    return {
      "md:pl-64": true,
      "md:pl-20": false,
    };
  }

  return {
    "md:pl-64": !iconOnly.value,
    "md:pl-20": iconOnly.value,
  };
});
// Search term for filtering menu items
const searchTerm = ref<string>("");

const pageTitle = useState<string>("pageTitle", () => "");
const pageIcon = useState<string>("pageIcon", () => "pi pi-hashtag");

const route = useRoute();

const menuGroup = computed(() => {
  if (menuItems.value.length === 0) return null;

  // Access route.fullPath to make this computed reactive to route changes
  const currentPath = route.fullPath;

  const matchingItem = menuItems.value.find(
    (item) => item.Link === currentPath,
  );

  return matchingItem?.MenuGroup ?? null;
});

// Watch for route changes to ensure reactivity
watch(
  () => route.fullPath,
  () => {
    // The computed will automatically update when route.fullPath changes
    // This watch ensures the computed is re-evaluated
  },
  { immediate: true },
);

// Methods
const toggleIcons = () => {
  iconOnly.value = !iconOnly.value;
  iconStyle.value = !iconStyle.value;
};

const closeDropdown = () => {
  showDropdownMenu.value = false;
};

// Fetch menu items
const fetch = async () => {
  try {
    const response = await $fetch("/scad/metamenus?OrderBy=SortOrder");
    // Icon is metadata-authored markup bound with v-html, so it is reduced to
    // the icon allow-list once here rather than at each render site.
    menuItems.value = (response.Results ?? []).map((item: MenuItem) => ({
      ...item,
      Icon: sanitizeHtml(item.Icon, "icon"),
    }));
  } catch (error) {
    // Error fetching menu items
  }
};

// Check screen size
const checkScreenSize = () => {
  if (import.meta.client && typeof window !== "undefined") {
    isDesktop.value = window.innerWidth >= 768;
  }
};

// Lifecycle hooks
onMounted(() => {
  // Only execute in client environment
  if (import.meta.client) {
    fetch();
    checkScreenSize();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", checkScreenSize);
    }
  }
});

// Menu item filtering
const filteredMenuItems = computed(() => {
  if (!searchTerm.value) {
    return menuItems.value.filter((m) => m.MenuGroup == null && m.Name);
  }

  const term = searchTerm.value.toLowerCase();
  return menuItems.value.filter(
    (item) => item.Name && item.Name.toLowerCase().includes(term),
  );
});

// Clean up event listener when component is unmounted
onUnmounted(() => {
  if (import.meta.client && typeof window !== "undefined") {
    window.removeEventListener("resize", checkScreenSize);
  }
});

// Expose necessary properties to the template
defineExpose({
  sidebarOpen,
  showDropdownMenu,
  menuItems,
  iconOnly,
  iconStyle,
  toggleIcons,
  closeDropdown,
  fetch,
});
</script>

<style scoped>
/* Tooltip styles */
.c-tooltip::before,
.c-tooltip::after {
  display: block;
  position: absolute;
  top: 0%;
  transform: translate(-50%);
  opacity: 0;
  transition: 0.3s;
}

.c-tooltip::before {
  content: "";
  right: calc(100%);
}

.c-tooltip::after {
  content: attr(aria-label);
  right: calc(100%);
  padding: 0.61em 0.93em;
  font-size: 12px;
  color: #047857;
  border-radius: 3px;
  background-color: white;
  border-color: #047857;
}

.c-tooltip:hover::before,
.c-tooltip:hover::after {
  opacity: 1;
  transform: translate(110%);
}

/* Responsive transitions */
.md\:w-64 {
  transition: width 0.3s ease;
}

.md\:w-20 {
  transition: width 0.3s ease;
}

.md\:pl-64 {
  transition: padding-left 0.3s ease;
}

.md\:pl-20 {
  transition: padding-left 0.3s ease;
}
</style>
